import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DynamicDialogRef, DynamicDialogConfig } from 'primeng/dynamicdialog';

// PrimeNG imports
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { MessageModule } from 'primeng/message';
import { FileUploadModule } from 'primeng/fileupload';

export interface TeamMember {
  id?: string;
  name: string;
  position: string;
  linkedinUrl: string;
  description: string;
  image: File | string | null;
  imagePreview?: string;
}

@Component({
  selector: 'app-team-member-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    ButtonModule,
    InputTextModule,
    MessageModule,
    FileUploadModule
  ],
  templateUrl: './team-member-dialog.component.html',
  styleUrl: './team-member-dialog.component.css'
})
export class TeamMemberDialogComponent implements OnInit {
  isEdit: boolean = false;
  imagePreview: string | null = null;

  // Form controls
  nameControl = new FormControl('', [Validators.required, Validators.minLength(2)]);
  positionControl = new FormControl('', [Validators.required, Validators.minLength(2)]);
  linkedinUrlControl = new FormControl('', [Validators.pattern(/^https?:\/\/(www\.)?linkedin\.com\/.+/)]);
  descriptionControl = new FormControl('', [Validators.required, Validators.minLength(10)]);
  imageControl = new FormControl<File | null>(null);

  teamMemberForm: FormGroup;

  constructor(
    private ref: DynamicDialogRef,
    private config: DynamicDialogConfig,
    private translateService: TranslateService
  ) {
    this.teamMemberForm = new FormGroup({
      name: this.nameControl,
      position: this.positionControl,
      linkedinUrl: this.linkedinUrlControl,
      description: this.descriptionControl,
      image: this.imageControl
    });
  }

  ngOnInit(): void {
    // Check if we're editing an existing team member
    if (this.config.data?.teamMember) {
      this.isEdit = true;
      const teamMember: TeamMember = this.config.data.teamMember;
      
      this.nameControl.setValue(teamMember.name);
      this.positionControl.setValue(teamMember.position);
      this.linkedinUrlControl.setValue(teamMember.linkedinUrl);
      this.descriptionControl.setValue(teamMember.description);
      
      if (teamMember.imagePreview) {
        this.imagePreview = teamMember.imagePreview;
      }
    }
  }

  onImageSelect(event: any): void {
    const file = event.files[0];
    if (file) {
      this.imageControl.setValue(file);
      
      // Create preview URL
      if (this.imagePreview && this.imagePreview.startsWith('blob:')) {
        URL.revokeObjectURL(this.imagePreview);
      }
      this.imagePreview = URL.createObjectURL(file);
    }
  }

  removeImage(): void {
    if (this.imagePreview && this.imagePreview.startsWith('blob:')) {
      URL.revokeObjectURL(this.imagePreview);
    }
    this.imagePreview = null;
    this.imageControl.setValue(null);
  }

  private markFormGroupTouched(): void {
    this.nameControl.markAsTouched();
    this.positionControl.markAsTouched();
    this.linkedinUrlControl.markAsTouched();
    this.descriptionControl.markAsTouched();
  }

  onSubmit(): void {
    if (this.teamMemberForm.invalid) {
      this.markFormGroupTouched();
      return;
    }

    const teamMemberData: TeamMember = {
      name: this.nameControl.value!,
      position: this.positionControl.value!,
      linkedinUrl: this.linkedinUrlControl.value!,
      description: this.descriptionControl.value!,
      image: this.imageControl.value,
      imagePreview: this.imagePreview || undefined
    };

    if (this.isEdit && this.config.data?.teamMember?.id) {
      teamMemberData.id = this.config.data.teamMember.id;
    }

    this.ref.close(teamMemberData);
  }

  onCancel(): void {
    this.ref.close();
  }

  // Error message methods
  getNameErrorMessage(): string {
    if (this.nameControl.errors?.['required']) {
      return this.translateService.instant('projectEdit.errors.teamMemberNameRequired');
    }
    if (this.nameControl.errors?.['minlength']) {
      return this.translateService.instant('projectEdit.errors.teamMemberNameMinLength');
    }
    return '';
  }

  getPositionErrorMessage(): string {
    if (this.positionControl.errors?.['required']) {
      return this.translateService.instant('projectEdit.errors.teamMemberPositionRequired');
    }
    if (this.positionControl.errors?.['minlength']) {
      return this.translateService.instant('projectEdit.errors.teamMemberPositionMinLength');
    }
    return '';
  }

  getLinkedinUrlErrorMessage(): string {
    if (this.linkedinUrlControl.errors?.['pattern']) {
      return this.translateService.instant('projectEdit.errors.teamMemberLinkedinUrlInvalid');
    }
    return '';
  }

  getDescriptionErrorMessage(): string {
    if (this.descriptionControl.errors?.['required']) {
      return this.translateService.instant('projectEdit.errors.teamMemberDescriptionRequired');
    }
    if (this.descriptionControl.errors?.['minlength']) {
      return this.translateService.instant('projectEdit.errors.teamMemberDescriptionMinLength');
    }
    return '';
  }
}